"use client"

import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import Image from "next/image"
import { cn } from "@/lib/utils"
import { useHelpPopup } from "@/stores/appStore"
import { useGuides } from "@/stores/appStore"
import { ChevronRightIcon, XIcon, ArrowLeftTriangleIcon } from "@/components/fun-ui/icons"
import { HelpGuides } from "@/stores/slices/guideSlice"
import { useSidebarCollapsed } from "@/stores/localStore"

type HelpPopupProps = {
  onClose?: () => void | Promise<void>;
  helpCenterRef?: React.RefObject<HTMLDivElement | null>;
}

export function HelpPopup({ onClose, helpCenterRef }: HelpPopupProps = {}) {
  const { open: storeOpen, scene, setOpen: setStoreOpen, setIsOpen: setStoreIsOpen } = useHelpPopup()
  const { markGuidesViewed, guides, guidesMap } = useGuides()
  const [isOpen, setIsOpen] = useState(false)
  const [arrowTop, setArrowTop] = useState<number>(0)
  const popupRef = useRef<HTMLDivElement>(null)
  const { collapsed } = useSidebarCollapsed()
  const prevCollapsedRef = useRef<boolean>(collapsed)

  // 从 guides 或 guidesMap[scene] 中提取 HelpGuides 数据
  const displayData = useMemo(() => {
    // 优先从 guidesMap[scene] 中获取，如果没有则从全局 guides 中获取
    const targetGuides = (scene && guidesMap[scene]?.guides) || guides
    if (targetGuides?.type === 'help') {
      return (targetGuides as HelpGuides).value
    }
    return null
  }, [guides, guidesMap, scene])

  // 监听 store 中的状态变化，用于外部触发显示
  useEffect(() => {
    if (storeOpen) {
      setIsOpen(true)
      // 重置 store 状态
      setStoreOpen(false)
    }
  }, [storeOpen, setStoreOpen])

  // 同步本地状态到 store
  useEffect(() => {
    setStoreIsOpen(isOpen)
  }, [isOpen, setStoreIsOpen])

  // 计算箭头位置的函数
  const calculateArrowPosition = useCallback(() => {
    if (!helpCenterRef?.current || !popupRef.current) return

    const helpCenterRect = helpCenterRef.current.getBoundingClientRect()
    const popupRect = popupRef.current.getBoundingClientRect()
    // 计算帮助中心菜单项的中心点相对于弹窗的位置
    const helpCenterCenterY = helpCenterRect.top + helpCenterRect.height / 2
    const popupTop = popupRect.top
    const arrowPosition = helpCenterCenterY - popupTop
    setArrowTop(arrowPosition)
  }, [helpCenterRef])

  // 初始化时立即计算箭头位置，避免看到延迟
  // 然后在动画完成后再次计算一次，确保位置准确
  useEffect(() => {
    if (!isOpen || !helpCenterRef?.current || !popupRef.current) {
      return
    }

    // 立即计算一次，避免看到延迟
    calculateArrowPosition()
    prevCollapsedRef.current = collapsed

    // 弹窗动画时长是 300ms，延迟 350ms 后再次计算确保位置准确
    const timer = setTimeout(() => {
      calculateArrowPosition()
    }, 350)

    return () => {
      clearTimeout(timer)
    }
  }, [isOpen, helpCenterRef, calculateArrowPosition, collapsed])

  // collapsed 状态变化时，延迟计算等待 sidebar 动画完成
  useEffect(() => {
    if (!isOpen || !helpCenterRef?.current || !popupRef.current) {
      return
    }

    // 如果 collapsed 状态没有变化，不处理
    if (prevCollapsedRef.current === collapsed) {
      return
    }

    // collapsed 状态变化时，延迟计算等待 sidebar 动画完成
    // sidebar 动画时长：收起 100ms，展开 300ms，所以延迟 350ms 确保动画完成
    const timer = setTimeout(() => {
      calculateArrowPosition()
    }, 350)

    // 更新 prevCollapsedRef
    prevCollapsedRef.current = collapsed

    return () => {
      clearTimeout(timer)
    }
  }, [collapsed, isOpen, helpCenterRef, calculateArrowPosition])

  const handleClose = async () => {
    setIsOpen(false)
    // 如果有 scene，调用 markGuidesViewed
    if (scene) {
      await markGuidesViewed(scene)
    }
    // 调用外部传入的关闭回调
    await onClose?.()
  }

  // 如果没有数据或未打开，不渲染组件
  if (!isOpen || !displayData) {
    return null
  }

  return (
    <div
      ref={popupRef}
      className={cn(
        "absolute left-full bottom-4 z-50",
        "w-[270px]",
        "rounded-[8px] border",
        "animate-in slide-in-from-left-5 fade-in-0 duration-300",
        "bg-[#F7F9FF] border border-border",
        "shadow-[0px_3.2px_8px_0px_rgba(0,0,0,0.08)]"
      )}
    >
      {/* 箭头图标 - 指向侧边栏的"帮助中心"菜单项 */}
      <div
        className={cn(
          "absolute -left-[7px]",
          "z-10",
          "flex items-center"
        )}
        style={{
          top: `${arrowTop}px`,
          transform: 'translateY(-50%)',
        }}
      >
        <ArrowLeftTriangleIcon size={7} style={{ height: '11px' }} />
      </div>

      {/* 关闭按钮 */}
      <div
        onClick={handleClose}
        className={cn(
          "absolute top-4 right-4 z-10",
          "flex items-center justify-center",
          "text-annotation-foreground hover:text-foreground",
          "cursor-pointer"
        )}
        aria-label="关闭"
      >
        <XIcon className="w-4 h-4" />
      </div>

      {/* 内容 */}
      <div className="p-4 mt-0.5">
        {/* 标题 */}
        <h2 className="text-sm font-semibold text-foreground">{displayData.title}</h2>

        {/* 图片 */}
        <div className="w-full flex justify-center">
          <Image
            src={displayData.image}
            alt={displayData.title}
            width={238}
            height={138}
            unoptimized
          />
        </div>

        {/* 描述文字 */}
        <div className="space-y-1">
          <p className="text-xs font-medium text-foreground">{displayData.subtitle}</p>
          <p className="text-xs text-muted-foreground text-justify">
            {displayData.content}
          </p>
        </div>

        {/* 链接列表 */}
        <div className="space-y-1.5 mt-4">
          {displayData.links.map((link, index) => (
            <WelcomeLink
              key={index}
              href={link.url}
              label={link.label}
              onClick={(e) => {
                if (link.url === "#") {
                  e.preventDefault()
                }
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function WelcomeLink({
  href,
  label,
  onClick,
}: {
  href: string
  label: string
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}) {
  return (
    <a
      href={href}
      onClick={onClick}
      className={cn(
        "flex items-center justify-between",
        "px-3 py-2 rounded-md",
        "bg-background hover:bg-muted",
        "transition-colors cursor-pointer",
        "border border-border",
        "group hover:border-border",
        "focus:outline-none"
      )}
      target="_blank"
    >
      <span className="text-xs text-foreground font-normal">{label}</span>
      <ChevronRightIcon className="text-annotation-foreground" />
    </a>
  )
}
